<template>
  <index-view-layout
    data-name="Project"
    :side-drawer-component="sideDrawerComponent"
    :should-hide-button="userType !== 'admin'"
    @search="searchProjects"
    searchable
  >
    <div class="w-full">
      <div class="mb-2 text-dark-gray" v-if="showSearchResults">
        Showing search results for "{{ searchTerm }}"
      </div>
      <stacked-tabs
        :tabs="tabs"
        :selected-tab="selectedTab"
        @tab-selected="selectedTab = $event"
        :hide-counts="showSearchResults"
        v-if="tabs"
      />
      <template v-if="$mq === 'sm'">
        <project-cell
          v-for="(project, index) in selectedTabProjects"
          :key="`${project.name}-${index}`"
          :project="project"
        />
      </template>
      <bdg-table-view
        :rows="selectedTabProjects"
        :columns="columns"
        :page-options="{
          isPaged: selectedTabProjects.length > 10,
          itemsPerPage: 10
        }"
        :loading="isLoading"
        v-else
      >
      </bdg-table-view>
    </div>
  </index-view-layout>
</template>

<script>
import IndexViewLayout from "../layouts/IndexViewLayout";
import ProjectForm from "../../forms/ProjectForm";
import StackedTabs from "../../elements/StackedTabs";
import BdgTableView from "../../elements/table-view/BdgTableView";
import ProjectCell from "../../elements/list-view/list-view-cells/ProjectCell";
import AddressCell from "../../elements/table-view/special-cells/AddressCell";
import GenericDeleteModal from "../../components/modals/GenericDeleteModal";
import { UserTypes } from "../../constants/user-enums";
import {
  crewSupervisorIndexTabs,
  projectsIndexTabs
} from "../../js-data/project-tab-groups";
import EmailCell from "../../elements/table-view/special-cells/EmailCell";

export default {
  name: "Projects",
  components: {
    ProjectCell,
    BdgTableView,
    StackedTabs,
    IndexViewLayout
  },
  data() {
    return {
      sideDrawerComponent: ProjectForm,
      selectedTab: null
    };
  },
  computed: {
    userType() {
      return this.$store.state.userType;
    },
    tabs() {
      if (this.userType === UserTypes.ADMIN) {
        return projectsIndexTabs;
      }
      if (this.userType === UserTypes.CREW_SUPERVISOR) {
        return crewSupervisorIndexTabs;
      }
      return null;
    },
    columnActions() {
      const actions = [
        {
          label: "view",
          clickAction: project => {
            this.viewProject(project);
          }
        },
        {
          label: "edit",
          clickAction: project => {
            this.$store.commit("setCreateEditComponentParams", {
              createEditComponent: ProjectForm,
              createEditComponentData: project,
              createEditComponentIsShown: true
            });
          }
        },
        {
          label: "delete",
          clickAction: project => {
            this.$store.commit("setConfirmationModalParams", {
              type: "delete",
              headerText: "Delete Confirmation",
              bodyComponent: GenericDeleteModal,
              bodyComponentData: {
                entityName: "project",
                deleteFunction: () => {
                  this.deleteProject(project);
                }
              },
              isShown: true
            });
          }
        }
      ];
      return this.userType === "admin" ? actions : actions.slice(0, 2);
    },
    columns() {
      return [
        { label: "project name", property: "name" },
        { label: "customer name", property: "primaryContactName" },
        { label: "address", property: "place", cellComponent: AddressCell },
        { label: "email", property: "nestedEmail", cellComponent: EmailCell },
        { label: "phone", property: "primaryPhoneNumber" },
        {
          label: "actions",
          property: "actions",
          actions: this.columnActions,
          additionalCellClasses: "w-px whitespace-no-wrap"
        }
      ];
    },
    showSearchResults() {
      return this.$store.state.Search.areSearchResultsShown;
    },
    searchTerm() {
      return this.$store.state.Search.searchTerm;
    },
    isGettingProjects() {
      return this.$store.state.Projects.isGettingProjects;
    },
    isSearchingProjects() {
      return this.$store.state.Search.isSearchingProjects;
    },
    isLoading() {
      return this.showSearchResults
        ? this.isSearchingProjects
        : this.isGettingProjects;
    },
    projects() {
      return this.$store.state.Projects.projects;
    },
    searchedProjects() {
      return this.$store.state.Search.searchedProjects;
    },
    displayedProjects() {
      let projects = this.showSearchResults
        ? this.searchedProjects
        : this.projects;
      if (projects) {
        projects = projects.map(project => {
          if (project.additional_contacts) {
            project.additional_contacts = project.additional_contacts.map(c => {
              return {
                ...c,
                label: c.name
              };
            });
          }

          project.nestedEmail = { email: project.email };

          return project;
        });

        projects.sort((a, b) => a.name.localeCompare(b.name));
      }
      return projects;
    },
    selectedTabProjects() {
      if (this.selectedTab && this.displayedProjects) {
        return this.displayedProjects.filter(
          project => project.type === this.selectedTab.slug
        );
      }
      if (this.userType === "designer") {
        return this.displayedProjects;
      }
      return [];
    }
  },
  watch: {
    searchedProjects(newVal, oldVal) {
      if (
        newVal !== oldVal &&
        newVal &&
        newVal.length &&
        this.showSearchResults
      ) {
        for (let i = 0; i < this.tabs.length; i++) {
          const tab = this.tabs[i];
          const projectsForTab = this.searchedProjects.filter(
            p => p.type === tab.slug
          );
          if (projectsForTab.length > 0) {
            this.selectedTab = tab;
            break;
          }
        }
      }
    }
  },
  created() {
    this.$store.dispatch("getProjects");
  },
  methods: {
    searchProjects(text) {
      if (text) {
        this.$store.dispatch("searchProjects", text);
      } else {
        this.$store.commit("setAreSearchResultsShown", false);
      }
    },
    viewProject(project) {
      this.$router.push({
        name: "projects-show",
        params: { id: project.id, project: project }
      });
    },
    deleteProject(project) {
      this.$store.dispatch("deleteProject", project).then(res => {
        this.$store.dispatch("handleApiResponse", {
          responseStatus: res,
          successText: "Project deleted successfully.",
          errorText: "An error occurred while attempting to delete Project.",
          afterSuccessFunction: () => {
            if (this.$store.state.Search.areSearchResultsShown) {
              this.$store.dispatch(
                "searchProjects",
                this.$store.state.Search.searchTerm
              );
            } else {
              this.$store.dispatch("getProjects");
            }
            this.$store.commit("resetConfirmationModalParams");
          }
        });
      });
    }
  }
};
</script>

<style scoped></style>
